import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useId, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import type { ApiError, MintInviteRequest, MintedInvite } from '../lib/api';
import { fetchMe, mintInvite, resendVerification } from '../lib/api';
import { copyToClipboard } from '../lib/clipboard';
import { useFocusTrap } from '../hooks/useFocusTrap';

/** Backend gate code (T-0087): the actor must verify their email to mint. */
const EMAIL_VERIFICATION_REQUIRED = 'email_verification_required';

interface InviteModalProps {
  onClose: () => void;
}

export function InviteModal({ onClose }: InviteModalProps) {
  const formId = useId();
  const modalRef = useRef<HTMLDivElement | null>(null);
  const [trustLevel, setTrustLevel] = useState<MintInviteRequest['trust_level']>(
    'cross_human_persistent',
  );
  const [intent, setIntent] = useState('');
  const [customName, setCustomName] = useState('');

  const mutation = useMutation<MintedInvite, ApiError, MintInviteRequest>({
    mutationFn: (body) => mintInvite(body),
  });

  useFocusTrap(modalRef, true);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!intent.trim()) return;
    mutation.mutate({
      trust_level: trustLevel,
      intent: intent.trim(),
      custom_name: customName.trim() || undefined,
    });
  }

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${formId}-title`}
      >
        <header className="modal-header">
          <h2 id={`${formId}-title`}>Invite</h2>
          <button
            type="button"
            className="modal-close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        {mutation.isSuccess ? (
          <MintedResult invite={mutation.data} onClose={onClose} />
        ) : (
          <form className="invite-form" onSubmit={handleSubmit}>
            <label className="invite-field">
              <span>Invite type</span>
              <select
                value={trustLevel}
                onChange={(e) =>
                  setTrustLevel(e.target.value as MintInviteRequest['trust_level'])
                }
              >
                <option value="cross_human_persistent">
                  Coworker — recurring access
                </option>
                <option value="cross_human_guest">
                  Coworker — guest access
                </option>
                <option value="self_device">
                  My device — trusted access
                </option>
              </select>
            </label>

            <label className="invite-field">
              <span>Intent</span>
              <textarea
                value={intent}
                onChange={(e) => setIntent(e.target.value)}
                placeholder="What is this invite for? e.g., Audit Q3 work."
                rows={3}
                required
                aria-required="true"
              />
            </label>

            <label className="invite-field">
              <span>Name shown for this collaborator (optional)</span>
              <input
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                placeholder="e.g., client-acme"
              />
            </label>

            {mutation.isError &&
              (mutation.error?.code === EMAIL_VERIFICATION_REQUIRED ? (
                // T-0087: no dead-end — a clear "verify to invite" prompt wired
                // to the same Resend the verify-email banner uses.
                <VerifyToInvitePrompt />
              ) : (
                <p className="invite-error" role="alert">
                  Couldn't create invite. Try again.
                </p>
              ))}

            <div className="invite-actions">
              <button
                type="button"
                className="invite-cancel"
                onClick={onClose}
                disabled={mutation.isPending}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="invite-submit"
                disabled={mutation.isPending || !intent.trim()}
              >
                {mutation.isPending ? 'Creating…' : 'Create invite'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Shown in place of the generic error when invite-mint is blocked by the
 * T-0087 email-verification gate (backend 403 `email_verification_required`).
 * Never a dead-end: it explains why and offers the SAME Resend the verify-email
 * banner uses (`resendVerification`, reading the address from the shared
 * `['me']` query). Once the user verifies, re-submitting the form mints.
 */
function VerifyToInvitePrompt() {
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: fetchMe });
  const resend = useMutation({
    mutationFn: () => resendVerification(me!.email),
  });
  const err = resend.error as ApiError | null;

  return (
    <div className="invite-verify-gate" role="alert">
      <p>
        <strong>Verify your email to invite collaborators.</strong>
      </p>
      <p>
        Inviting others is available once you’ve confirmed your email
        {me?.email ? (
          <>
            {' '}
            — we sent a link to <strong>{me.email}</strong>
          </>
        ) : null}
        . Everything else stays available in the meantime.
      </p>
      {resend.isSuccess ? (
        <p
          className="invite-verify-sent"
          role="status"
          aria-live="polite"
        >
          Sent — check your inbox.
        </p>
      ) : (
        <>
          {err && (
            <p className="invite-verify-note">
              {err.status === 429
                ? 'Please wait a moment before resending.'
                : 'Couldn’t resend just now — try again shortly.'}
            </p>
          )}
          <button
            type="button"
            className="invite-submit"
            onClick={() => resend.mutate()}
            disabled={resend.isPending || !me?.email}
          >
            {resend.isPending ? 'Sending…' : 'Resend verification email'}
          </button>
        </>
      )}
    </div>
  );
}

interface MintedResultProps {
  invite: MintedInvite;
  onClose: () => void;
}

type InviteCopyTarget = 'url' | 'token';

const inviteCopyRowStyle = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'center',
} as const;

const inviteCopyInputStyle = {
  flex: '1 1 auto',
  minWidth: 0,
} as const;

const inviteCopyButtonStyle = {
  minWidth: '44px',
  minHeight: '44px',
} as const;

function MintedResult({ invite, onClose }: MintedResultProps) {
  const [copied, setCopied] = useState<InviteCopyTarget | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);

  useEffect(() => {
    if (copied === null) return;
    const timer = window.setTimeout(() => setCopied(null), 2000);
    return () => window.clearTimeout(timer);
  }, [copied]);

  async function copyInviteValue(target: InviteCopyTarget, value: string) {
    // The URL and token are already shown in read-only, selectable fields
    // above, so on failure just guide the operator to copy by hand.
    if (await copyToClipboard(value)) {
      setCopied(target);
      setCopyFailed(false);
    } else {
      setCopyFailed(true);
      setCopied(null);
    }
  }

  const copyStatus = copied === null ? '' : 'Copied!';

  return (
    <div className="invite-result">
      <p>Invite created for <strong>{invite.persona}</strong>.</p>
      <CopyableInviteField
        label="Invite URL (share with coworker)"
        inputLabel="Invite URL"
        value={invite.invite_url}
        onCopy={() => {
          void copyInviteValue('url', invite.invite_url);
        }}
      />
      <CopyableInviteField
        label="Invite token (for manual setup)"
        inputLabel="Invite token"
        value={invite.invite_token}
        onCopy={() => {
          void copyInviteValue('token', invite.invite_token);
        }}
      />
      {copyStatus && (
        <p className="invite-expires" role="status" aria-live="polite">
          {copyStatus}
        </p>
      )}
      {copyFailed && (
        <p className="invite-error" role="alert">
          Copy failed — select the text above and copy it manually.
        </p>
      )}
      {invite.expires_at && (
        <p className="invite-expires">
          Expires {new Date(invite.expires_at).toLocaleString()}.
        </p>
      )}
      <div className="invite-actions">
        <button type="button" className="invite-submit" onClick={onClose}>
          Done
        </button>
      </div>
    </div>
  );
}

interface CopyableInviteFieldProps {
  label: string;
  inputLabel: string;
  value: string;
  onCopy: () => void;
}

function CopyableInviteField({
  label,
  inputLabel,
  value,
  onCopy,
}: CopyableInviteFieldProps) {
  return (
    <div className="invite-field">
      <span>{label}</span>
      <div style={inviteCopyRowStyle}>
        <input
          type="text"
          value={value}
          readOnly
          aria-label={inputLabel}
          style={inviteCopyInputStyle}
        />
        <button
          type="button"
          className="invite-cancel"
          style={inviteCopyButtonStyle}
          aria-label={`Copy ${inputLabel}`}
          onClick={onCopy}
        >
          Copy
        </button>
      </div>
    </div>
  );
}
